/**
 * 组件实例 metadata 读取工具。
 *
 * 文件职责：
 * 1. 统一读取 imported-model 投影到 Object3D 的运行时 metadata；
 * 2. 兼容旧版 `userData.glbHierarchy` 与 `userData.importedSelectionTarget`；
 * 3. 只返回 plain data，不暴露 Three.js 对象给上层 Mate / Move / Pattern 业务。
 */

import { SceneAuxiliaryObjectRegistry } from '../../../scene-object';

/** imported-model 元数据字段名。 */
const IMPORTED_MODEL_METADATA_KEY = 'importedModel';

/** `.puppy` 运行时语义字段名。 */
const PUPPY_RUNTIME_METADATA_KEY = 'puppy';

/**
 * 可读取 metadata 的对象最小协议。
 *
 * 设计说明：
 * 工具函数只依赖 `metadata/userData` 两个普通字段，避免把 `three` 与 `three/webgpu`
 * 的具体 Object3D 类型差异扩散到公共解析逻辑中。
 */
export interface ComponentInstanceMetadataHost {
  /** three.js 运行时 metadata。 */
  readonly metadata?: unknown;
  /** three.js 兼容 userData。 */
  readonly userData?: unknown;
}

/**
 * 带父链的组件实例 metadata 宿主。
 *
 * 设计说明：
 * 组件 transform 解析需要识别“资源仍保留在场景对象树中，但不再代表可交互组件”的子树。
 * 这类子树通过既有 `skipSelectionSubtree` 与统一辅助身份进入组件遍历过滤；不改变专用工具的拾取规则。
 */
export interface ComponentInstanceMetadataHierarchyHost extends ComponentInstanceMetadataHost {
  /** three.js 父对象；不存在父对象时表示当前对象已经脱离场景或位于遍历根。 */
  readonly parent?: ComponentInstanceMetadataHierarchyHost | null;
}

/** 组件实例遍历需要跳过的既有子树标记。 */
const COMPONENT_INSTANCE_SKIP_SELECTION_SUBTREE_KEY = 'skipSelectionSubtree';

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录。
 */
export function isComponentInstanceMetadataRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * 判断对象自身是否声明整棵子树不参与组件实例遍历，包括纯显示和可交互辅助手柄。
 *
 * @param object 待判断的场景对象最小协议。
 * @returns 当前对象是否是应被跳过的子树根。
 */
export function shouldSkipComponentInstanceTraversalSubtree(
  object: ComponentInstanceMetadataHost,
): boolean {
  const userData = isComponentInstanceMetadataRecord(object.userData) ? object.userData : {};
  // 组件身份索引与通用变换解析共用边界；辅助手柄仍保留自己的专用拾取与拖拽入口。
  return userData[COMPONENT_INSTANCE_SKIP_SELECTION_SUBTREE_KEY] === true
    || SceneAuxiliaryObjectRegistry.isRoot(object);
}

/**
 * 判断对象自身或任一祖先是否处于组件实例遍历禁用子树中。
 *
 * @param object 待判断的场景对象。
 * @returns 对象是否不应作为 Move/Mate 的组件解析来源。
 */
export function isInsideSkippedComponentInstanceTraversalSubtree(
  object: ComponentInstanceMetadataHierarchyHost,
): boolean {
  let current: ComponentInstanceMetadataHierarchyHost | null | undefined = object;
  while (current) {
    if (shouldSkipComponentInstanceTraversalSubtree(current)) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

/**
 * 规范化字符串值。
 *
 * @param value 待读取值。
 * @returns 去除首尾空白后的字符串；无效时返回空字符串。
 */
export function normalizeComponentInstanceStringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 从未知记录中读取字符串字段。
 *
 * @param record 源记录。
 * @param key 字段名。
 * @returns 非空字符串；缺失或记录无效时返回空字符串。
 */
export function readComponentInstanceStringValue(record: unknown, key: string): string {
  if (!isComponentInstanceMetadataRecord(record)) {
    return '';
  }
  return normalizeComponentInstanceStringValue(record[key]);
}

/**
 * 从未知记录中读取布尔字段。
 *
 * @param record 源记录。
 * @param key 字段名。
 * @returns 布尔值；字段不存在或记录无效时返回 false。
 */
export function readComponentInstanceBooleanValue(record: unknown, key: string): boolean {
  return isComponentInstanceMetadataRecord(record) && record[key] === true;
}

/**
 * 读取 Object3D.metadata.importedModel。
 *
 * @param object 场景对象最小协议。
 * @returns imported-model metadata；不存在时返回空记录。
 */
export function readComponentInstanceImportedModelMetadata(
  object: ComponentInstanceMetadataHost,
): Record<string, unknown> {
  const metadata = isComponentInstanceMetadataRecord(object.metadata) ? object.metadata : {};
  const importedModelMetadata = metadata[IMPORTED_MODEL_METADATA_KEY];
  return isComponentInstanceMetadataRecord(importedModelMetadata) ? importedModelMetadata : {};
}

/**
 * 读取 Object3D.metadata.importedModel.hierarchy.meta。
 *
 * @param object 场景对象最小协议。
 * @returns hierarchy.meta；不存在时返回空记录。
 */
export function readComponentInstanceImportedModelHierarchyMeta(
  object: ComponentInstanceMetadataHost,
): Record<string, unknown> {
  const importedModelMetadata = readComponentInstanceImportedModelMetadata(object);
  const hierarchy = isComponentInstanceMetadataRecord(importedModelMetadata.hierarchy)
    ? importedModelMetadata.hierarchy
    : {};
  return isComponentInstanceMetadataRecord(hierarchy.meta) ? hierarchy.meta : {};
}

/**
 * 读取旧版 GLB hierarchy.meta。
 *
 * @param object 场景对象最小协议。
 * @returns hierarchy.meta；不存在时返回空记录。
 */
export function readComponentInstanceGlbHierarchyMeta(
  object: ComponentInstanceMetadataHost,
): Record<string, unknown> {
  const userData = isComponentInstanceMetadataRecord(object.userData) ? object.userData : {};
  const hierarchy = isComponentInstanceMetadataRecord(userData.glbHierarchy) ? userData.glbHierarchy : {};
  return isComponentInstanceMetadataRecord(hierarchy.meta) ? hierarchy.meta : {};
}

/**
 * 读取 `.puppy` 运行时语义 metadata。
 *
 * 设计说明：
 * `.puppy` 离线包重载后的组件 occurrence 可能只保存在 `userData.puppy.assemblyNodeId`。
 * 这里把它收口到 component-instance 元数据读取层，避免 Move/Rotate/Mate/索引各自理解离线包结构。
 *
 * @param object 场景对象最小协议。
 * @returns `.puppy` 运行时 metadata；不存在时返回空记录。
 */
export function readComponentInstancePuppyMetadata(
  object: ComponentInstanceMetadataHost,
): Record<string, unknown> {
  const userData = isComponentInstanceMetadataRecord(object.userData) ? object.userData : {};
  const puppyMetadata = userData[PUPPY_RUNTIME_METADATA_KEY];
  return isComponentInstanceMetadataRecord(puppyMetadata) ? puppyMetadata : {};
}

/**
 * 读取 imported-model selectionTarget。
 *
 * @param object 场景对象最小协议。
 * @returns selectionTarget；不存在时返回空记录。
 */
export function readComponentInstanceImportedModelSelectionTarget(
  object: ComponentInstanceMetadataHost,
): Record<string, unknown> {
  const importedModelMetadata = readComponentInstanceImportedModelMetadata(object);
  const selectionTarget = importedModelMetadata.selectionTarget;
  return isComponentInstanceMetadataRecord(selectionTarget) ? selectionTarget : {};
}

/**
 * 判断对象是否携带 imported-model 语义。
 *
 * @param object 场景对象最小协议。
 * @returns 是否包含新旧任一 imported-model metadata。
 */
export function hasComponentInstanceImportedModelMetadata(
  object: ComponentInstanceMetadataHost,
): boolean {
  return Object.keys(readComponentInstanceImportedModelMetadata(object)).length > 0
    || Object.keys(readComponentInstanceGlbHierarchyMeta(object)).length > 0;
}
